// Small duck for StartRentalPage. Fetches the transaction, tracks the
// in-flight handoff submission. Similar patterns in the other three
// handoff pages (CompleteReturn, ReportDamage, ReportPickupIssue).

import { storableError } from '../../util/errors';
import { postHandoffTransition } from '../../util/pickupCode';

const FETCH_TX_REQUEST = 'app/StartRentalPage/FETCH_TX_REQUEST';
const FETCH_TX_SUCCESS = 'app/StartRentalPage/FETCH_TX_SUCCESS';
const FETCH_TX_ERROR = 'app/StartRentalPage/FETCH_TX_ERROR';

const SUBMIT_REQUEST = 'app/StartRentalPage/SUBMIT_REQUEST';
const SUBMIT_SUCCESS = 'app/StartRentalPage/SUBMIT_SUCCESS';
const SUBMIT_ERROR = 'app/StartRentalPage/SUBMIT_ERROR';
const SUBMIT_RESET = 'app/StartRentalPage/SUBMIT_RESET';

const initialState = {
  transaction: null,
  fetchInProgress: false,
  fetchError: null,
  submitInProgress: false,
  submitError: null,
  submitSuccess: false,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_TX_REQUEST:
      return { ...state, fetchInProgress: true, fetchError: null };
    case FETCH_TX_SUCCESS:
      return { ...state, fetchInProgress: false, transaction: payload };
    case FETCH_TX_ERROR:
      return { ...state, fetchInProgress: false, fetchError: payload };
    case SUBMIT_REQUEST:
      return { ...state, submitInProgress: true, submitError: null, submitSuccess: false };
    case SUBMIT_SUCCESS:
      return { ...state, submitInProgress: false, submitSuccess: true };
    case SUBMIT_ERROR:
      return { ...state, submitInProgress: false, submitError: payload };
    case SUBMIT_RESET:
      return { ...state, submitError: null, submitSuccess: false };
    default:
      return state;
  }
}

export const submitReset = () => ({ type: SUBMIT_RESET });

// Thunk: run the confirm-pickup transition and update the redux state.
// Returns a promise so the UI can branch on specific error shapes
// (e.g. INVALID_PICKUP_CODE) and clear the input inline.
export const submitStartRental = ({ transactionId, pickupCodeEntered, pickupPhotoKeys }) => dispatch => {
  dispatch({ type: SUBMIT_REQUEST });
  return postHandoffTransition({
    transactionId,
    transition: 'transition/confirm-pickup',
    orderData: {
      pickupCodeEntered,
      pickupPhotos: pickupPhotoKeys,
    },
  })
    .then(response => {
      dispatch({ type: SUBMIT_SUCCESS });
      return response;
    })
    .catch(err => {
      dispatch({ type: SUBMIT_ERROR, payload: storableError(err) });
      throw err;
    });
};

// Thunk: fetch the transaction via user-scope SDK. Includes only the
// relationships our page needs to render (title, who the other party is,
// booking dates). Metadata is not returned to the user-scope SDK for
// any handoff-related secrets by design — that's checked elsewhere.
export const loadData = params => (dispatch, getState, sdk) => {
  dispatch({ type: FETCH_TX_REQUEST });
  return sdk.transactions
    .show({
      id: params.id,
      include: ['customer', 'provider', 'listing', 'listing.author', 'booking'],
    })
    .then(resp => {
      const tx = resp?.data?.data;
      const included = resp?.data?.included || [];
      // Flatten relationships so the page component doesn't have to
      // walk the JSON:API entity graph.
      const customerRel = tx?.relationships?.customer?.data;
      const providerRel = tx?.relationships?.provider?.data;
      const listingRel = tx?.relationships?.listing?.data;
      const bookingRel = tx?.relationships?.booking?.data;
      const customer = included.find(
        e => e.type === 'user' && e.id.uuid === customerRel?.id?.uuid
      );
      const provider = included.find(
        e => e.type === 'user' && e.id.uuid === providerRel?.id?.uuid
      );
      const listing = included.find(
        e => e.type === 'listing' && e.id.uuid === listingRel?.id?.uuid
      );
      const booking = included.find(
        e => e.type === 'booking' && e.id.uuid === bookingRel?.id?.uuid
      );
      dispatch({
        type: FETCH_TX_SUCCESS,
        payload: { ...tx, customer, provider, listing, booking },
      });
      return tx;
    })
    .catch(e => {
      dispatch({ type: FETCH_TX_ERROR, payload: storableError(e) });
      throw e;
    });
};
